import { useI18n } from '../../i18n';
// ============================================
// AuditLogsPage Component
// System audit logs and activity tracking
// ============================================

import { useState, useMemo } from 'react';
import { PageHeader, PageContainer, Pagination, ExportButton } from '../../components/shared';
import { SelectDropdown } from '../../components/shared/SelectDropdown';
import { useAuditLogs, useAuditLogStats, useAuditEntityTypes, useExport } from '../../hooks';
import type { AuditLog, AuditAction } from '@conai/shared-types';

// ============================================
// Types
// ============================================

interface AuditLogItem {
  id: string;
  userName: string;
  action: AuditAction;
  entityType: string;
  entityId: string;
  details: string;
  ipAddress: string;
  timestamp: string;
}

interface FilterState {
  action: AuditAction | 'all';
  entityType: string;
  search: string;
}

// ============================================
// Stats Component
// ============================================

function StatsCard({ title, value, color }: { title: string; value: string | number; color: string }) {
  const { t } = useI18n();
  return (
    <div className="rounded-xl bg-white p-5 shadow-sm border border-slate-100">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
      <p className={`mt-2 text-lg font-bold sm:text-2xl ${color}`}>{value}</p>
    </div>
  );
}

// ============================================
// Badge Components
// ============================================

function ActionBadge({ action }: { action: AuditAction }) {
  const { t } = useI18n();
  // Màu theo mức hệ quả, không theo nhóm chức năng: đỏ = mất dữ liệu hoặc mất
  // tiền (DELETE / REFUND / VOID / ACCOUNT_DELETION), cam = động vào tiền hoặc
  // tồn (ADJUST / CANCEL), còn lại là thao tác thường. Người soát log quét mắt
  // tìm dòng đỏ trước.
  const config: Record<AuditAction, { bg: string; text: string }> = {
    CREATE: { bg: 'bg-green-100', text: 'text-green-700' },
    UPDATE: { bg: 'bg-blue-100', text: 'text-blue-700' },
    DELETE: { bg: 'bg-red-100', text: 'text-red-700' },
    LOGIN: { bg: 'bg-purple-100', text: 'text-purple-700' },
    LOGOUT: { bg: 'bg-slate-100', text: 'text-slate-700' },
    // Truy cập dữ liệu cá nhân — không phải thao tác thường, và là thứ kiểm
    // toán NĐ13/GDPR đi tìm. Cho màu riêng để lọc bằng mắt được.
    ACCESS: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
    APPROVE: { bg: 'bg-green-100', text: 'text-green-700' },
    COMPLETE: { bg: 'bg-green-100', text: 'text-green-700' },
    CANCEL: { bg: 'bg-amber-100', text: 'text-amber-700' },
    ADJUST: { bg: 'bg-amber-100', text: 'text-amber-700' },
    REFUND: { bg: 'bg-red-100', text: 'text-red-700' },
    VOID: { bg: 'bg-red-100', text: 'text-red-700' },
    ACCOUNT_DELETION: { bg: 'bg-red-100', text: 'text-red-700' },
    PASSWORD_CHANGE: { bg: 'bg-purple-100', text: 'text-purple-700' },
    PASSWORD_RESET: { bg: 'bg-purple-100', text: 'text-purple-700' },
  };
  const style = config[action] || { bg: 'bg-slate-100', text: 'text-slate-600' };
  
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}>
      {action}
    </span>
  );
}

function EntityBadge({ type }: { type: string }) {
  const { t } = useI18n();
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 capitalize">
      {type.replace(/_/g, ' ')}
    </span>
  );
}

// ============================================
// Filter Bar Component
// ============================================

function FilterBar({
  filters,
  onChange,
  entityTypeOptions,
}: {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  entityTypeOptions: string[];
}) {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl bg-white p-4 shadow-sm border border-slate-100">
      <div className="w-full min-w-0 sm:w-auto sm:flex-1 sm:min-w-[200px]">
        <input
          type="text"
          placeholder={t('searchLogs')}
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          enterKeyHint="search"
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
        />
      </div>

      {/* 2 droplist chung 1 hàng trên mobile; dùng SelectDropdown để không mở popup native */}
      <div className="grid w-full grid-cols-2 gap-3 sm:flex sm:w-auto sm:items-center sm:gap-4">
        <SelectDropdown
          value={filters.action}
          onChange={(v) => onChange({ ...filters, action: v as AuditAction | 'all' })}
          options={[
            // Đồng bộ enum BE (bug #138) — EXPORT không còn hợp lệ, BE trả 400.
            // BE xác nhận 04/09/2026: chưa từng có code nào ghi EXPORT.
            { value: 'all', label: t('allActions') },
            { value: 'CREATE', label: t('create') },
            { value: 'UPDATE', label: t('update') },
            { value: 'DELETE', label: t('auditlogsDelete') },
            { value: 'LOGIN', label: t('login') },
            { value: 'LOGOUT', label: t('logout') },
            // Truy cập dữ liệu cá nhân (mục #84). BE validate ?action= theo
            // enum nên giá trị này ăn ngay khi bản có ACCESS lên QA; trước đó
            // lọc sẽ trả 400 — đó là dấu hiệu QA chưa có bản mới, không phải lỗi FE.
            { value: 'ACCESS', label: t('auditActionAccess') },
            { value: 'APPROVE', label: 'Approve' },
            { value: 'CANCEL', label: 'Cancel' },
            { value: 'REFUND', label: 'Refund' },
            { value: 'VOID', label: 'Void' },
            { value: 'ADJUST', label: 'Adjust' },
            { value: 'COMPLETE', label: 'Complete' },
            { value: 'PASSWORD_CHANGE', label: 'Password Change' },
            { value: 'PASSWORD_RESET', label: 'Password Reset' },
            { value: 'ACCOUNT_DELETION', label: 'Account Deletion' },
          ]}
          className="min-w-0 sm:min-w-[150px]"
        />
        <SelectDropdown
          value={filters.entityType}
          onChange={(v) => onChange({ ...filters, entityType: v })}
          options={[
            { value: '', label: t('entityType') },
            ...entityTypeOptions.map((et) => ({
              value: et,
              label: et.charAt(0).toUpperCase() + et.slice(1),
            })),
          ]}
          /**
           * `sm:w-40` (160px) cắt mất chữ: giá trị dài nhất BE trả về là
           * `CustomerAgeVerification` — 23 ký tự, cần ~210px ở `text-sm` cộng
           * padding và mũi tên. 240px chứa vừa nó, vẫn hẹp hơn ô lọc Actions
           * bên cạnh nên hàng lọc không lệch nhịp.
           *
           * Giá trị dài hơn nữa (BE thêm entity mới) sẽ XUỐNG DÒNG trong menu
           * chứ không bị cắt — xem `whitespace-normal break-words` ở
           * `SelectDropdown`. Nên đây là chỉnh cho vừa mắt, không phải chốt
           * chặn duy nhất.
           */
          className="min-w-0 sm:w-[240px]"
        />
      </div>
    </div>
  );
}

// ============================================
// Table Component
// ============================================

function AuditLogsTable({ data, isLoading, hasError }: { data: AuditLogItem[]; isLoading: boolean; hasError: boolean }) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className="rounded-xl bg-white shadow-sm border border-slate-100 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (hasError) {
    return null;
  }

  if (data.length === 0) {
    return (
      <div className="rounded-xl bg-white shadow-sm border border-slate-100 p-8 text-center">
        <p className="text-slate-500">{t('noAuditLogsFound')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('timestamp')}</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('user')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('action')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('entity')}</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('details')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('ipAddress')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {data.map((item) => (
            <tr key={item.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">
                {new Date(item.timestamp).toLocaleString()}
              </td>
              <td className="px-6 py-4">
                <span className="font-medium text-slate-900">{item.userName}</span>
              </td>
              <td className="px-6 py-4 text-center">
                <ActionBadge action={item.action} />
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex flex-col items-center gap-1">
                  <EntityBadge type={item.entityType} />
                  {item.entityId && (
                    <span className="text-xs text-slate-500 font-mono">{item.entityId.slice(0, 8)}...</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                {item.details}
              </td>
              <td className="px-6 py-4 text-center text-sm text-slate-500 font-mono">
                {item.ipAddress || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function AuditLogsPage() {
  const { t } = useI18n();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<FilterState>({
    action: 'all',
    entityType: '',
    search: '',
  });
  const pageSize = 10;

  // API Queries
  // Tên param theo hợp đồng BE 05/09: `limit` (trần 100) + `resource_type`.
  // `page_size`/`entity_type` của bản cũ đã bỏ — django-filter bỏ qua IM LẶNG
  // tên nó không biết, nên gửi kèm "cho chắc" chỉ che mất việc tên nào đang
  // thật sự có tác dụng.
  const { data: logsData, isLoading, error: logsError } = useAuditLogs({
    page: currentPage,
    limit: pageSize,
    action: filters.action !== 'all' ? filters.action : undefined,
    resource_type: filters.entityType || undefined,
    // Tìm trên email/tên/SĐT của NGƯỜI XEM, không phải của khách bị xem.
    search: filters.search || undefined,
  });
  const { data: statsData, isLoading: statsLoading, error: statsError } = useAuditLogStats();

  // Xuất nhật ký. `/audit/export/` TRƯỚC ĐÂY KHÔNG TỒN TẠI — mọi cú bấm export
  // đều 404 từ đầu; BE làm mới trong đợt 05/09 và dùng chung code lọc với
  // list, nên tên param không thể lệch. Truyền kèm đúng bộ lọc đang xem để
  // file khớp những gì trên màn hình. Quá 10.000 dòng BE trả 400 yêu cầu thu
  // hẹp khoảng ngày.
  const { triggerExport, isExporting } = useExport({
    endpoint: '/audit/export/',
    params: {
      action: filters.action !== 'all' ? filters.action : undefined,
      resource_type: filters.entityType || undefined,
      search: filters.search || undefined,
    },
    filePrefix: 'audit_logs',
  });

  const error = logsError || statsError;

  // Default stats values
  const stats = useMemo(() => {
    const rawData = logsData as any;
    const pag = rawData?.pagination ?? rawData?.data?.pagination;
    const countVal = pag?.total_items ?? rawData?.count ?? rawData?.total ?? rawData?.data?.total ?? rawData?.data?.count ?? 0;
    const sData = statsData as any;
    const deleteActions = sData?.delete_actions ?? sData?.data?.delete_actions ?? sData?.actions_by_type?.DELETE ?? 0;
    const today = sData?.today_activity ?? sData?.data?.today_activity ?? sData?.today ?? sData?.data?.today ?? 0;
    const users = sData?.unique_users ?? sData?.data?.unique_users ?? 0;
    const total = sData?.total_logs ?? sData?.data?.total_logs ?? countVal;

    return {
      totalLogs: total,
      todayActivity: today,
      uniqueUsers: users,
      criticalActions: deleteActions,
    };
  }, [statsData, logsData]);

  // Map audit logs
  const auditLogs: AuditLogItem[] = useMemo(() => {
    const rawData = logsData as any;
    const list = Array.isArray(rawData?.items)
      ? rawData.items
      : Array.isArray(rawData?.data?.items)
      ? rawData.data.items
      : Array.isArray(rawData?.results)
      ? rawData.results
      : Array.isArray(rawData?.data)
      ? rawData.data
      : [];

    return list.map((log: any) => ({
      id: log.id,
      userName: log.user_email ?? log.actor_email ?? 'System',
      action: log.action,
      // BE đính chính 19/09/2026: API audit KHÔNG có field nào tên `entity_id`
      // hay `entity_type` — tên thật là `resource_id` / `resource_type`. Nhánh
      // dự phòng cũ không bao giờ trúng, chỉ khiến người đọc tưởng hai tên kia
      // có thật rồi đem đi làm tham số lọc (django-filter nuốt im lặng, đúng
      // cái bẫy đã ghi trong project memory).
      entityType: log.resource_type ?? '',
      entityId: log.resource_id ?? '',
      details: formatDetails(log),
      ipAddress: log.ip ?? log.ip_address ?? '',
      timestamp: log.created_at,
    }));
  }, [logsData]);

  const { data: entityTypesData } = useAuditEntityTypes();
  const entityTypeOptions = useMemo(() => {
    const raw = entityTypesData as any;
    const list: string[] = raw?.entity_types ?? raw?.data?.entity_types ?? [];
    const set = new Set<string>(Array.isArray(list) ? list : []);
    if (filters.entityType) set.add(filters.entityType);
    return Array.from(set).sort();
  }, [entityTypesData, filters.entityType]);

  // Pagination
  const rawData = logsData as any;
  const pagination = rawData?.pagination ?? rawData?.data?.pagination;
  const totalItems =
    pagination?.total_items ??
    pagination?.count ??
    rawData?.count ??
    rawData?.total ??
    rawData?.data?.total ??
    rawData?.data?.count ??
    0;
  const totalPages =
    pagination?.total_pages ?? Math.max(1, Math.ceil(totalItems / pageSize));

  return (
    <PageContainer>
      <PageHeader
        title={t('auditLogs')}
        subtitle={t('trackSystemActivityAndUserActions')}
        actions={<ExportButton onExport={triggerExport} isLoading={isExporting} />}
      />

      {/* Error Message */}
      {!!logsError && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadAuditLogs')}</p>
          <p className="mt-1 text-red-600">
            {logsError instanceof Error ? logsError.message : 'An error occurred while loading audit logs.'}
          </p>
        </div>
      )}

      {/* Stats Row */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title={t('totalLogs')} value={statsLoading ? '...' : stats.totalLogs} color="text-purple-700" />
        <StatsCard title={t('todaysActivity')} value={statsLoading ? '...' : stats.todayActivity} color="text-blue-600" />
        <StatsCard title={t('uniqueUsers')} value={statsLoading ? '...' : stats.uniqueUsers} color="text-green-600" />
        <StatsCard title={t('deleteActions')} value={statsLoading ? '...' : stats.criticalActions} color="text-red-600" />
      </div>

      {/* Filters */}
      <div className="mt-6">
        <FilterBar filters={filters} onChange={(f) => { setFilters(f); setCurrentPage(1); }} entityTypeOptions={entityTypeOptions} />
      </div>

      {/* Table */}
      <div className="mt-6">
        <AuditLogsTable data={auditLogs} isLoading={isLoading} hasError={!!logsError} />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </PageContainer>
  );
}

// ============================================
// Helper Functions
// ============================================

/**
 * Mô tả một dòng log.
 *
 * Nhánh ACCESS (mục #84) là quan trọng nhất và phải nói ĐÚNG SỐ KHÁCH: BE ghi
 * MỘT dòng cho mỗi REQUEST, nên xem hàng đợi duyệt 18+ một lần là một dòng
 * log dù nhìn thấy ảnh giấy tờ của mười khách. Đếm số dòng ≠ số khách bị lộ —
 * đó chính là con số kiểm toán NĐ13/GDPR hỏi tới, nên hiện ngay ở cột mô tả
 * thay vì bắt người soát mở từng dòng ra đếm mảng.
 */
function formatDetails(log: any): string {
  const details = log.details as
    | { endpoint?: string; customer_id?: string; customer_ids?: string[] }
    | null
    | undefined;

  if (log.action === 'ACCESS') {
    const count = details?.customer_ids?.length;
    if (typeof count === 'number') {
      return `Xem hàng đợi 18+ — ${count} khách trong lần xem này`;
    }
    if (details?.customer_id) return `Xem hồ sơ 18+ của khách ${details.customer_id}`;
    if (details?.endpoint === 'customer_detail') return 'Xem hồ sơ khách hàng';
    return 'Truy cập dữ liệu cá nhân';
  }

  /**
   * Đổi danh mục hàng loạt (`POST /products/bulk-update/`).
   *
   * `AuditLogListSerializer` chỉ trả `details` (alias của `new_values`), KHÔNG
   * có `old_values` — muốn thấy danh mục cũ phải mở bản chi tiết. Vì vậy BE
   * nhét sẵn `category_name` vào `new_values` để dòng trong danh sách ít nhất
   * đọc được nó chuyển ĐI ĐÂU.
   *
   * Không có nhánh này thì dòng hiện ra là "Changed: category, category_name,
   * bulk_operation_id" — liệt kê tên field thay vì nói chuyện gì đã xảy ra.
   */
  const bulkCategory = log.new_values as
    | { category?: string | null; category_name?: string | null; bulk_operation_id?: string }
    | null
    | undefined;
  if (bulkCategory?.bulk_operation_id && 'category' in (bulkCategory ?? {})) {
    const name = bulkCategory.category_name;
    return bulkCategory.category === null || !name
      ? 'Bulk: category removed'
      : `Bulk: category → ${name}`;
  }

  if (log.new_values && Object.keys(log.new_values).length > 0) {
    const keys = Object.keys(log.new_values).slice(0, 3);
    return `Changed: ${keys.join(', ')}${keys.length < Object.keys(log.new_values).length ? '...' : ''}`;
  }
  return `${log.action} on ${log.resource_type ?? 'Entity'}`;
}
